import React, { useCallback, useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  setStep,
  addStepsData,
} from "app/lib/store/slices/steps/assetsLiabilities";
import { useAddAssetsLiabilities } from "app/hooks/assetsLiabilities/useAddAssetsLiabilities";
import { ASSST_LIABILITY } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import RadioButton from "app/components/common/buttons/RadioButton";
import AddMore from "app/components/common/buttons/AddMore";
import { useNavigate } from "react-router-dom";
import { useDeleteItem } from "app/hooks/useDeleteItem";
import DynamicRealEstate from "app/components/Individual/applicant/assetsLiabilities/DynamicRealEstate";
import DynamicSuperannuation from "app/components/Individual/applicant/assetsLiabilities/DynamicSuperannuation";
import { handleApiError } from "app/utils/handleApiError";

interface FormData {
  realeStateType: string;
  superannuationType: string;
  realState: { dbId?: string; detail: string; value: string }[];
  superannuation: { dbId?: string; nameOfFund: string; value: string }[];
}

const PartOne: React.FC = () => {
  const dispatch = useDispatch();
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const navigate = useNavigate();
  const { mutate: submitAssetsLiabilities, isPending } =
    useAddAssetsLiabilities();
  const { formOne, ...others } = useSelector(
    (state: RootState) => state.assetsLiabilities.stepsData
  ) || {
    formOne: { realState: [], superannuation: [] },
  };
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    register,
    setValue,
    setError,
    clearErrors,
  } = useForm<FormData>({
    defaultValues: {
      realState: [{ detail: "", value: "" }],
      superannuation: [{ nameOfFund: "", value: "" }],
      realeStateType: "no",
      superannuationType: "no",
    },
  });
  const realestateValue = watch("realeStateType");
  const superannuationValue = watch("superannuationType");

  const onSubmit = (data: FormData) => {
    if (!savedIds?.applicantID) {
      toast.error("Applicant id is required");
      return;
    }
    const payload: any = {};
    const { realState, superannuation, realeStateType, superannuationType } =
      data || {};

    if (realeStateType === "yes") {
      payload.realState = realState?.map((item: any) => ({
        id: item.dbId,
        detail: item.detail,
        value: Number(item.value),
      }));
    } else {
      payload.realState = null;
    }
    if (superannuationType === "yes") {
      payload.superannuation = superannuation?.map((item: any) => ({
        id: item.dbId,
        nameOfFund: item.nameOfFund,
        value: Number(item.value),
      }));
    } else {
      payload.superannuation = null;
    }

    submitAssetsLiabilities(
      {
        url: `${ASSST_LIABILITY}/${savedIds?.applicantID}/first`,
        data: payload,
      },
      {
        onSuccess: (data: any) => {
          const { realStates, superannuations } = data || {};
          const realStateMapped = realStates?.map((item: any) => ({
            dbId: item.id,
            detail: item.detail,
            value: item.value?.toString(),
          }));
          const superannuationMapped = superannuations?.map((item: any) => ({
            dbId: item.id,
            nameOfFund: item.nameOfFund,
            value: item.value?.toString(),
          }));

          dispatch(
            addStepsData({
              ...others,
              formOne: {
                realState: realeStateType === "no" ? [] : realStateMapped || [],
                superannuation:
                  superannuationType === "no" ? [] : superannuationMapped || [],
                realeStateType,
                superannuationType,
              },
            })
          );
          dispatch(setStep(2));
        },
        onError: (error: any) => {
          handleApiError(error);
        },
      }
    );
  };
  const { fields, append, remove } = useFieldArray({
    control,
    name: "realState",
  });
  const {
    fields: superannuationFields,
    append: superannuationAppend,
    remove: superannuationRemove,
  } = useFieldArray({
    control,
    name: "superannuation",
  });

  const handleAddRealState = useCallback(() => {
    append({ detail: "", value: "" });
  }, [append]);

  const handleAddSuperannuation = useCallback(() => {
    superannuationAppend({ nameOfFund: "", value: "" });
  }, [superannuationAppend]);
  useEffect(() => {
    if (formOne && Object.keys(formOne).length > 0) {
      reset({
        ...(formOne?.realeStateType &&
          formOne?.realeStateType && {
            realeStateType: formOne.realeStateType,
          }),
        ...(formOne?.superannuationType &&
          formOne.superannuationType && {
            superannuationType: formOne.superannuationType,
          }),
        realState: formOne?.realState?.length
          ? formOne.realState
          : [{ detail: "", value: "" }],
        superannuation: formOne?.superannuation?.length
          ? formOne.superannuation
          : [{ nameOfFund: "", value: "" }],
      });
    }
  }, [formOne, reset]);

  const handleBack = () => {
    navigate(`/income-details`);
  };

  return (
    <>
      <ProgressBar progress={0} MB={7} />
      <div className="w-full  sm:w-[70%] xl:w-[50%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Real Estate/Superannuation" />

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          <div className="text-left mb-5">
            <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
              Do you have any real estate?
            </h6>
            <div className="flex gap-5">
              <Controller
                name="realeStateType"
                control={control}
                rules={{ required: "Please select an option" }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex gap-5">
                    <RadioButton
                      id="realestate_yes"
                      name="realeStateType"
                      value="yes"
                      label="Yes"
                      checked={value === "yes"}
                      onChange={onChange}
                    />
                    <RadioButton
                      id="realestate_no"
                      name="realeStateType"
                      value="no"
                      label="No"
                      checked={value === "no"}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>
            {errors.realeStateType && (
              <ErrorsMessage
                title={errors.realeStateType.message}
                className="!text-left"
              />
            )}
          </div>

          {realestateValue === "yes" && (
            <>
              <DynamicRealEstate
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                fields={fields}
                deleteItem={deleteItem}
                realState="realState"
                applicantID={savedIds?.applicantID as string}
                remove={remove}
                deleteIsPending={deleteIsPending}
              />
              {fields.length < 3 && (
                <div className="flex items-center justify-end mt-6 mb-4 sm:mb-2">
                  <AddMore onClick={handleAddRealState} />
                </div>
              )}
            </>
          )}

          <div className="text-left mb-5">
            <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
              Do you have any superannuation?
            </h6>
            <div className="flex gap-5">
              <Controller
                name="superannuationType"
                control={control}
                rules={{ required: "Please select an option" }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex gap-5">
                    <RadioButton
                      id="superannuation_yes"
                      name="superannuationType"
                      value="yes"
                      label="Yes"
                      checked={value === "yes"}
                      onChange={onChange}
                    />
                    <RadioButton
                      id="superannuation_no"
                      name="superannuationType"
                      value="no"
                      label="No"
                      checked={value === "no"}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>
            {errors.superannuationType && (
              <ErrorsMessage
                title={errors.superannuationType.message}
                className="!text-left"
              />
            )}
          </div>

          {superannuationValue === "yes" && (
            <>
              <DynamicSuperannuation
                control={control}
                errors={errors}
                fields={superannuationFields}
                deleteItem={deleteItem}
                superannuation="superannuation"
                applicantID={savedIds?.applicantID as string}
                remove={superannuationRemove}
                deleteIsPending={deleteIsPending}
              />
              {superannuationFields.length < 3 && (
                <div className="flex items-center justify-end mt-6">
                  <AddMore onClick={handleAddSuperannuation} />
                </div>
              )}
            </>
          )}

          <div className="flex justify-between my-10">
            <PreviousButtom onClick={handleBack}>Previous</PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartOne;
